import React, { useRef } from "react";

import classnames from "classnames";
import PropTypes from "prop-types";

import { useTranslation } from "components/i18n/source";
import { useBootstrap } from "components/store/source";
import { Button, Section, Text } from "components/ui/source";

import { csrfToken, csrfParam } from "../../shared/api/base_api";
import { messageMahalo, messageType } from "../../util/messageMahalo";

import styles from "./Accounts.module.scss";

function OAuthContainer({ setLoading, logSubmit, theme, redirectPath }) {
  const { t } = useTranslation();
  const {
    mahalo,
    featureFlags: { cognitoGoogleEnabled, cognitoAppleEnabled },
  } = useBootstrap();

  const oAuthFormRef = useRef(null);
  const prefix = "modals.accounts.oAuth";
  const urlParams = window.location.search;
  const searchParams = new URLSearchParams(urlParams);
  const showCognitoGoogleLogin =
    cognitoGoogleEnabled || searchParams.has("cognitoGoogleEnabled");
  const showCognitoAppleLogin =
    cognitoAppleEnabled || searchParams.has("cognitoAppleEnabled");

  const data = {
    apple: {
      redirectUrl: `/accounts/auth/${
        showCognitoAppleLogin ? "cognito_apple" : "apple"
      }`,
      imageUrl:
        "https://images.sonder.com/image/upload/v1596816163/catalina/icons/apple_color.svg",
      alt: "Apple logo",
    },
    google: {
      redirectUrl: `/accounts/auth/${
        showCognitoGoogleLogin ? "cognito_google" : "google_oauth2"
      }`,
      imageUrl:
        "https://images.sonder.com/image/upload/v1596816163/catalina/icons/google_color.svg",
      alt: "Google logo",
    },
    linkedIn: {
      redirectUrl: "/accounts/auth/linkedin",
      imageUrl:
        "https://images.sonder.com/image/upload/v1597272466/catalina/icons/linkedin_large_color.svg",
      alt: "LinkedIn logo",
    },
  };

  // Return button title based on current theme
  function getButtonString(medium) {
    if (theme === "signup") {
      return t(`${prefix}.${medium}`);
    } else {
      return t(`${prefix}.login`, {
        medium: medium,
      });
    }
  }

  // Need to use form to make CSRF token work for redireciton
  async function onSubmitOAuth(medium) {
    if (!oAuthFormRef.current) return;

    await logSubmit(theme === "signup" ? "oauth_signup" : "oauth_login");

    messageMahalo({ type: messageType.oauthAttempt, body: medium });

    if (!mahalo) {
      setLoading(true);

      const redirectUrl = redirectPath || window?.location?.href || "/";

      oAuthFormRef.current.method = "post";
      oAuthFormRef.current.action = `${
        data[medium]?.redirectUrl
      }?redirect_path=${encodeURIComponent(redirectUrl)}`;
      oAuthFormRef.current.submit();
    }
  }

  return (
    <Section className={classnames(styles.oauth_container, styles[theme])}>
      <form method="post" ref={oAuthFormRef} data-testid="oauthForm">
        <input name={csrfParam()} value={csrfToken()} type="hidden" />
      </form>

      <Button
        type="popup_signup"
        upperCase={false}
        onClick={() => onSubmitOAuth("google")}
      >
        <img src={data.google.imageUrl} alt={data.google.alt} />
        <Text element="span" types={["subtitles"]}>
          {getButtonString("google")}
        </Text>
      </Button>
      {theme !== "signup" && (
        <Button
          className={styles.linkedIn}
          type="popup_signup"
          upperCase={false}
          onClick={() => onSubmitOAuth("linkedIn")}
        >
          <img src={data.linkedIn.imageUrl} alt={data.linkedIn.alt} />
          <Text element="span" types={["subtitles"]}>
            {getButtonString("linkedIn")}
          </Text>
        </Button>
      )}
      <Button
        type="popup_signup"
        upperCase={false}
        onClick={() => onSubmitOAuth("apple")}
      >
        <img src={data.apple.imageUrl} alt={data.apple.alt} />
        <Text element="span" types={["subtitles"]}>
          {getButtonString("apple")}
        </Text>
      </Button>
    </Section>
  );
}

OAuthContainer.propTypes = {
  setLoading: PropTypes.func.isRequired,
  logSubmit: PropTypes.func.isRequired,
  theme: PropTypes.oneOf(["signup", "login"]),
  exitIntentProps: PropTypes.object,
  redirectPath: PropTypes.string,
};

OAuthContainer.defaultProps = {
  setLoading: () => {},
  logSubmit: async () => {},
  theme: "signup",
  exitIntentProps: {},
};

export default OAuthContainer;
